
import React, { useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Network } from "lucide-react";
import { MicroSkillAnalysis } from "@/types/quiz";

interface PersonalKnowledgeGraphProps {
  microSkillsAnalysis: MicroSkillAnalysis[];
  subject: string | null;
}

const PersonalKnowledgeGraph: React.FC<PersonalKnowledgeGraphProps> = ({ 
  microSkillsAnalysis,
  subject 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    if (!canvasRef.current || !microSkillsAnalysis || microSkillsAnalysis.length === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set up the graph visualization
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.min(centerX, centerY) - 50;
    
    // Draw the center node (main subject)
    ctx.beginPath();
    ctx.arc(centerX, centerY, 30, 0, 2 * Math.PI);
    ctx.fillStyle = '#6366f1'; // Primary indigo color
    ctx.fill();
    
    // Draw text for center node
    ctx.font = 'bold 14px sans-serif';
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(subject || 'Subject', centerX, centerY);
    
    // Get node positions in a circle around the center
    const nodeCount = microSkillsAnalysis.length;
    const nodePositions = microSkillsAnalysis.map((_, index) => {
      const angle = (index / nodeCount) * 2 * Math.PI;
      const x = centerX + maxRadius * Math.cos(angle);
      const y = centerY + maxRadius * Math.sin(angle);
      return { x, y };
    });
    
    // Draw connections first (so they're behind nodes)
    microSkillsAnalysis.forEach((skill, index) => {
      const position = nodePositions[index];
      
      // Draw line to center
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(position.x, position.y);
      
      // Line thickness based on strength
      const lineWidth = skill.strength === 'strong' ? 3 : skill.strength === 'moderate' ? 2 : 1;
      ctx.lineWidth = lineWidth;
      
      // Line color based on strength
      ctx.strokeStyle = skill.strength === 'strong' 
        ? '#22c55e' // Green for strong
        : skill.strength === 'moderate' 
          ? '#3b82f6' // Blue for moderate
          : '#ef4444'; // Red for weak
          
      ctx.stroke();
    });
    
    // Draw the nodes for each micro skill
    microSkillsAnalysis.forEach((skill, index) => {
      const position = nodePositions[index];
      
      // Node radius based on question count (scaled)
      const radius = 15 + Math.min(skill.totalQuestions * 2, 15);
      
      // Draw node
      ctx.beginPath();
      ctx.arc(position.x, position.y, radius, 0, 2 * Math.PI);
      
      // Fill color based on strength
      ctx.fillStyle = skill.strength === 'strong' 
        ? 'rgba(34, 197, 94, 0.8)' // Green for strong
        : skill.strength === 'moderate' 
          ? 'rgba(59, 130, 246, 0.8)' // Blue for moderate
          : 'rgba(239, 68, 68, 0.8)'; // Red for weak
      
      ctx.fill();
      
      // Draw text for percentage
      ctx.font = 'bold 12px sans-serif';
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${skill.percentage.toFixed(0)}%`, position.x, position.y);
      
      // Draw skill name
      ctx.font = '12px sans-serif';
      ctx.fillStyle = '#1f2937'; // Dark gray
      ctx.textAlign = 'center';
      
      // Position text based on angle for readability
      const angle = (index / nodeCount) * 2 * Math.PI;
      const textOffsetY = angle > Math.PI / 2 && angle < 3 * Math.PI / 2 ? radius + 20 : -radius - 10;
      
      // Wrap text if too long
      const words = skill.skill.split(' ');
      if (words.length > 1) {
        ctx.fillText(words[0], position.x, position.y + textOffsetY);
        ctx.fillText(words.slice(1).join(' '), position.x, position.y + textOffsetY + 16);
      } else {
        ctx.fillText(skill.skill, position.x, position.y + textOffsetY);
      }
    });
    
  }, [microSkillsAnalysis, subject, canvasRef]);
  
  if (!microSkillsAnalysis || microSkillsAnalysis.length === 0) {
    return null;
  }
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl flex items-center text-primary">
          <Network className="mr-2 h-5 w-5" />
          Personal Knowledge Graph
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-gray-500 mb-4">
          This visualization shows your knowledge strengths and areas for improvement. 
          Larger nodes represent topics with more questions. 
          Green connections show strong areas, blue shows moderate knowledge, and red highlights focus areas.
        </div>
        <div className="flex justify-center">
          <canvas 
            ref={canvasRef} 
            width="700" 
            height="500" 
            className="max-w-full h-auto border border-gray-100 rounded-lg"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalKnowledgeGraph;
